import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, getDocs, addDoc, updateDoc, doc, Timestamp, limit,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { loadTeamSettings } from './teamSettings';
import { toDate, workedMinutes, overlappingRecords } from './timesheetUtils';
import { submitShiftRequest } from './shiftRequestsCloud';
import './Shifts.css';

/* ── helpers ── */
const fmtTime = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
const fmtDate = (d) => d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const fmtShortDate = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const pad = (n) => String(n).padStart(2, '0');
const localISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toLocalInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
const elapsed = (ms) => {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
};
const elapsedShort = (ms) => {
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};
const fmtMin = (min) => `${Math.floor(min / 60)}h ${min % 60}m`;
function weekMonday(d = new Date()) {
  const m = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  m.setDate(m.getDate() - (m.getDay() === 0 ? 6 : m.getDay() - 1));
  return m;
}

const MISTAP_WINDOW_MIN = 5; // "Clocked in by mistake?" stays offered this long
const SHORT_SHIFT_MIN = 2;   // clocking out sooner than this asks first

// A query that needs a composite index fails until the index is built — fall back to
// a single-field query (filtered here) so the page never silently shows nothing.
async function getDocsOrFallback(primary, fallback) {
  try {
    return await getDocs(primary());
  } catch (err) {
    if (err?.code !== 'failed-precondition') throw err;
    console.warn('Index not ready — using a simpler query', err);
    return getDocs(fallback());
  }
}

async function findOpenShift(uid) {
  const snap = await getDocs(query(
    collection(db, 'shifts'), where('userId', '==', uid), where('status', 'in', ['active', 'on-break']), limit(1),
  ));
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

const ShiftClock = () => {
  const { user, role, loading } = useAuth();
  const navigate = useNavigate();

  const [activeShift, setActiveShift] = useState(null); // current active shift doc
  const [recentShifts, setRecentShifts] = useState([]); // completed, last 7 days, newest first
  const [now, setNow] = useState(new Date());
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingShift, setLoadingShift] = useState(true);
  const [notice, setNotice] = useState('');           // one-line feedback under the clock card
  const [shortOut, setShortOut] = useState(false);    // "only 1 min — remove it instead?" prompt
  const timerRef = useRef(null);
  const actionLock = useRef(false);                   // blocks a second tap before React re-renders

  // Hour-change / missed-shift request state
  const [reqModal, setReqModal] = useState(null); // { mode: 'edit'|'missed', shift?, pending? }
  const [reqClockIn, setReqClockIn] = useState('');
  const [reqClockOut, setReqClockOut] = useState('');
  const [reqReason, setReqReason] = useState('');
  const [reqSaving, setReqSaving] = useState(false);
  const [reqResult, setReqResult] = useState(null); // 'submitted' | 'updated' after sending
  const [reqError, setReqError] = useState('');
  const [reqOverlapNote, setReqOverlapNote] = useState(''); // shown once; the next tap sends anyway
  const [myRequests, setMyRequests] = useState({}); // shiftId → latest request for it
  const [myMissed, setMyMissed] = useState([]);     // my pending "missed shift" requests
  // Deactivated in the planner's 👥 Team → no new clock-ins (an open shift can still be finished)
  const [inactive, setInactive] = useState(false);

  // Live clock
  useEffect(() => {
    timerRef.current = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Auth guard
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
  }, [user, loading, navigate]);

  // Load active shift + recent history
  const loadShifts = useCallback(async () => {
    if (!user) return;
    setLoadingShift(true);
    try {
      const open = await findOpenShift(user.uid);
      setActiveShift(open);
      setNotes(open?.notes || '');

      // Completed shifts of the last 7 days (covers this Mon–Sun week too)
      const since = new Date();
      since.setDate(since.getDate() - 7);
      const snap = await getDocsOrFallback(
        () => query(collection(db, 'shifts'), where('userId', '==', user.uid), where('status', '==', 'completed'),
          where('clockIn', '>=', Timestamp.fromDate(since)), orderBy('clockIn', 'desc'), limit(60)),
        () => query(collection(db, 'shifts'), where('userId', '==', user.uid)),
      );
      const list = [];
      snap.forEach((d) => {
        const s = { id: d.id, ...d.data() };
        const inAt = toDate(s.clockIn);
        if (s.status === 'completed' && inAt && inAt >= since) list.push(s);
      });
      list.sort((a, b) => toDate(b.clockIn) - toDate(a.clockIn));
      setRecentShifts(list);
    } catch (err) {
      console.error('Failed to load shifts:', err);
    }
    setLoadingShift(false);
  }, [user]);

  useEffect(() => { loadShifts(); }, [loadShifts]);

  // My requests: latest per shift, plus pending "missed shift" ones
  const loadMyRequests = useCallback(async () => {
    if (!user) return;
    try {
      const snap = await getDocsOrFallback(
        () => query(collection(db, 'shiftRequests'), where('userId', '==', user.uid), orderBy('createdAt', 'desc'), limit(40)),
        () => query(collection(db, 'shiftRequests'), where('userId', '==', user.uid)),
      );
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0));
      const map = {};
      list.forEach((r) => { if (r.shiftId && !map[r.shiftId]) map[r.shiftId] = r; });
      setMyRequests(map);
      setMyMissed(list.filter((r) => r.type === 'new-shift' && r.status === 'pending'));
    } catch (err) {
      console.error('Failed to load requests:', err);
    }
  }, [user]);

  useEffect(() => { loadMyRequests(); }, [loadMyRequests]);

  useEffect(() => {
    if (!user) return;
    loadTeamSettings().then(t => setInactive(!!t.inactive[user.uid]));
  }, [user]);

  // ── Requests ──
  const openReqModal = (shift) => {
    const pending = myRequests[shift.id]?.status === 'pending' ? myRequests[shift.id] : null;
    const inDate = toDate(pending ? pending.requestedClockIn : shift.clockIn);
    const outDate = toDate(pending ? pending.requestedClockOut : shift.clockOut);
    setReqClockIn(inDate ? toLocalInput(inDate) : '');
    setReqClockOut(outDate ? toLocalInput(outDate) : '');
    setReqReason(pending?.reason || '');
    setReqResult(null); setReqError(''); setReqOverlapNote('');
    setReqModal({ mode: 'edit', shift, pending });
  };
  const openMissedModal = () => {
    setReqClockIn(''); setReqClockOut(''); setReqReason('');
    setReqResult(null); setReqError(''); setReqOverlapNote('');
    setReqModal({ mode: 'missed' });
  };

  const handleSubmitRequest = async () => {
    if (actionLock.current || reqSaving) return;
    if (!reqClockIn || !reqClockOut) { setReqError('Please fill in both times.'); return; }
    if (!reqReason.trim()) { setReqError('Please give a reason.'); return; }
    const newIn = new Date(reqClockIn);
    const newOut = new Date(reqClockOut);
    if (newOut <= newIn) { setReqError('Clock-out must be after clock-in.'); return; }
    if (newIn > new Date()) { setReqError("That start time hasn't happened yet."); return; }
    // Times that land on another of their own records would count those hours twice — say so once
    if (!reqOverlapNote) {
      const mine = [...recentShifts, ...(activeShift ? [activeShift] : [])];
      const clash = overlappingRecords(mine, { userId: user.uid, start: newIn, end: newOut, ignoreId: reqModal.shift?.id });
      if (clash.length) {
        const c = clash[0], a = toDate(c.clockIn), b = toDate(c.clockOut);
        setReqOverlapNote(`These times overlap your ${fmtShortDate(a)} shift (${fmtTime(a)} – ${b ? fmtTime(b) : 'now'}). A manager will see that — tap again to send anyway.`);
        return;
      }
    }
    actionLock.current = true;
    setReqSaving(true); setReqError('');
    const missed = reqModal.mode === 'missed';
    try {
      const { updated } = await submitShiftRequest({
        type: missed ? 'new-shift' : 'edit',
        shiftId: missed ? null : reqModal.shift.id,
        userId: user.uid,
        userName: user.displayName || user.email,
        userEmail: user.email,
        userRole: role || 'employee',
        submittedBy: user.uid,
        submittedByName: user.displayName || user.email,
        submittedByRole: role || 'employee',
        originalClockIn: missed ? null : reqModal.shift.clockIn,
        originalClockOut: missed ? null : (reqModal.shift.clockOut || null),
        requestedClockIn: Timestamp.fromDate(newIn),
        requestedClockOut: Timestamp.fromDate(newOut),
        totalMinutes: Math.round((newOut - newIn) / 60000),
        reason: reqReason.trim(),
      });
      setReqResult(updated ? 'updated' : 'submitted');
      loadMyRequests();
      setTimeout(() => setReqModal(null), 1800);
    } catch (err) {
      console.error('Submit request failed:', err);
      setReqError(err?.code === 'already-pending'
        ? 'A request for this is already waiting for approval — a manager will review it.'
        : 'Could not send the request. Please try again.');
    }
    setReqSaving(false);
    actionLock.current = false;
  };

  /* ── Actions ── */
  const handleClockIn = async () => {
    if (actionLock.current || busy || !user || inactive) return;
    actionLock.current = true;
    setBusy(true); setNotice('');
    try {
      // Already clocked in (a double tap, another device, or a manager did it)? Use that shift.
      const open = await findOpenShift(user.uid);
      if (open) {
        setActiveShift(open);
        setNotes(open.notes || '');
        setNotice(`You were already clocked in (since ${fmtTime(toDate(open.clockIn))}).`);
      } else {
        const nowTs = Timestamp.now();
        const data = {
          userId: user.uid,
          userName: user.displayName || '(no name)',
          userEmail: user.email,
          userRole: role || 'employee',
          clockIn: nowTs,
          clockOut: null,
          breaks: [],
          totalMinutes: 0,
          date: localISO(new Date()),
          status: 'active',
          notes: '',
        };
        const ref = await addDoc(collection(db, 'shifts'), data);
        setActiveShift({ id: ref.id, ...data });
        setNotes('');
      }
    } catch (err) {
      console.error('Clock in failed:', err);
      setNotice('Could not clock in. Please try again.');
    }
    setBusy(false);
    actionLock.current = false;
  };

  // Cancel a mistaken clock-in: kept as a deleted record (audit) but counts no hours
  const handleCancelClockIn = async () => {
    if (actionLock.current || busy || !activeShift) return;
    actionLock.current = true;
    setBusy(true);
    try {
      await updateDoc(doc(db, 'shifts', activeShift.id), {
        status: 'deleted',
        prevStatus: activeShift.status,
        clockOut: Timestamp.now(),
        totalMinutes: 0,
        deletedAt: Timestamp.now(),
        deletedBy: user.uid,
        deletedByName: user.displayName || user.email || '',
        deleteReason: 'Clocked in by mistake — cancelled by the employee',
      });
      setActiveShift(null);
      setNotes('');
      setShortOut(false);
      setNotice('Clock-in cancelled — no hours recorded.');
    } catch (err) {
      console.error('Cancel clock-in failed:', err);
      setNotice('Could not cancel. Please try again.');
    }
    setBusy(false);
    actionLock.current = false;
  };

  const doClockOut = async () => {
    if (actionLock.current || busy || !activeShift) return;
    actionLock.current = true;
    setBusy(true); setShortOut(false);
    try {
      const clockOutTs = Timestamp.now();
      const totalMinutes = workedMinutes(toDate(activeShift.clockIn), clockOutTs.toDate(), activeShift.breaks || []);
      await updateDoc(doc(db, 'shifts', activeShift.id), {
        clockOut: clockOutTs,
        status: 'completed',
        totalMinutes,
        notes: notes.trim(),
      });
      setActiveShift(null);
      setNotes('');
      setNotice(`Clocked out — ${fmtMin(totalMinutes)} recorded.`);
      loadShifts();
    } catch (err) {
      console.error('Clock out failed:', err);
      // e.g. a manager already clocked this shift out — show the real state
      setNotice('Could not clock out — refreshing your shift.');
      loadShifts();
    }
    setBusy(false);
    actionLock.current = false;
  };

  const handleClockOut = () => {
    if (busy || !activeShift) return;
    const minutesIn = (now - toDate(activeShift.clockIn)) / 60000;
    if (minutesIn < SHORT_SHIFT_MIN) { setShortOut(true); return; }
    if (!window.confirm('Are you sure you want to clock out?')) return;
    doClockOut();
  };

  const handleStartBreak = async () => {
    if (busy || !activeShift || activeShift.status !== 'active') return;
    setBusy(true);
    try {
      const breaks = [...(activeShift.breaks || []), { start: Timestamp.now(), end: null }];
      await updateDoc(doc(db, 'shifts', activeShift.id), { breaks, status: 'on-break' });
      setActiveShift((prev) => ({ ...prev, breaks, status: 'on-break' }));
    } catch (err) {
      console.error('Start break failed:', err);
      setNotice('Could not start the break. Please try again.');
    }
    setBusy(false);
  };

  const handleEndBreak = async () => {
    if (busy || !activeShift || activeShift.status !== 'on-break') return;
    setBusy(true);
    try {
      const breaks = [...(activeShift.breaks || [])];
      const lastIdx = breaks.length - 1;
      breaks[lastIdx] = { ...breaks[lastIdx], end: Timestamp.now() };
      await updateDoc(doc(db, 'shifts', activeShift.id), { breaks, status: 'active' });
      setActiveShift((prev) => ({ ...prev, breaks, status: 'active' }));
    } catch (err) {
      console.error('End break failed:', err);
      setNotice('Could not end the break. Please try again.');
    }
    setBusy(false);
  };

  /* ── Computed values ── */
  const clockInAt = activeShift ? toDate(activeShift.clockIn) : null;
  const getElapsed = () => (clockInAt ? elapsed(now - clockInAt) : '00:00:00');

  const getBreakTotal = () => {
    if (!activeShift?.breaks?.length) return 0;
    let ms = 0;
    activeShift.breaks.forEach((b) => {
      const start = toDate(b.start);
      const end = toDate(b.end) || now;
      if (start) ms += end - start;
    });
    return ms;
  };

  const getWorkTime = () => (clockInAt ? elapsed(Math.max(0, now - clockInAt - getBreakTotal())) : '00:00:00');
  const canCancelClockIn = !!clockInAt && (now - clockInAt) / 60000 < MISTAP_WINDOW_MIN && !(activeShift.breaks || []).length;

  // This Mon–Sun week (the pay week), from the completed shifts loaded above
  const monday = weekMonday(now);
  const weekShifts = recentShifts.filter((s) => toDate(s.clockIn) >= monday);
  const weekTotal = weekShifts.reduce((sum, s) => sum + (s.totalMinutes || 0), 0);

  if (loading || !user) return null;

  return (
    <div className="shift-page">
      <div className="shift-page__inner">

        {/* Header */}
        <div className="shift-header">
          <div className="shift-header__greeting">
            <span className="shift-header__wave">👋</span>
            <span>Hello, <strong>{user.displayName || 'Team Member'}</strong></span>
          </div>
          <div className="shift-header__datetime">
            <div className="shift-header__date">{fmtDate(now)}</div>
            <div className="shift-header__time">{fmtTime(now)}</div>
          </div>
        </div>

        {loadingShift ? (
          <div className="shift-loading">Loading your shift…</div>
        ) : (
          <>
            {/* Clock Card */}
            <div className={`shift-clock ${activeShift ? (activeShift.status === 'on-break' ? 'shift-clock--break' : 'shift-clock--active') : ''}`}>
              {!activeShift && inactive ? (
                /* ── Deactivated: no new shifts ── */
                <div className="shift-clock__idle">
                  <div className="shift-clock__icon">⏸</div>
                  <h2 className="shift-clock__title">Clock-in is paused for your account</h2>
                  <p className="shift-clock__sub">A manager has marked you inactive. If you're back on the schedule, ask them to reactivate you.</p>
                </div>
              ) : !activeShift ? (
                /* ── Not clocked in ── */
                <div className="shift-clock__idle">
                  <div className="shift-clock__icon">🕐</div>
                  <h2 className="shift-clock__title">Ready to start your shift?</h2>
                  <p className="shift-clock__sub">Clock in to begin tracking your hours.</p>
                  <button className="shift-clock__btn shift-clock__btn--in" onClick={handleClockIn} disabled={busy}>
                    {busy ? 'Starting…' : '▶ Clock In'}
                  </button>
                </div>
              ) : (
                /* ── Clocked in ── */
                <div className="shift-clock__active">
                  <div className="shift-clock__status-badge">
                    <span className={`shift-clock__dot ${activeShift.status === 'on-break' ? 'shift-clock__dot--break' : 'shift-clock__dot--active'}`} />
                    {activeShift.status === 'on-break' ? '☕ On Break' : '🟢 Clocked In'}
                  </div>

                  <div className="shift-clock__timer">{getElapsed()}</div>
                  <div className="shift-clock__label">Total Elapsed</div>

                  <div className="shift-clock__stats">
                    <div className="shift-clock__stat">
                      <span className="shift-clock__stat-val">{getWorkTime()}</span>
                      <span className="shift-clock__stat-lbl">Work Time</span>
                    </div>
                    <div className="shift-clock__stat-divider" />
                    <div className="shift-clock__stat">
                      <span className="shift-clock__stat-val">{elapsedShort(getBreakTotal())}</span>
                      <span className="shift-clock__stat-lbl">Break Time</span>
                    </div>
                    <div className="shift-clock__stat-divider" />
                    <div className="shift-clock__stat">
                      <span className="shift-clock__stat-val">{activeShift.breaks?.length || 0}</span>
                      <span className="shift-clock__stat-lbl">Breaks</span>
                    </div>
                  </div>

                  <div className="shift-clock__meta">
                    Clocked in at <strong>{clockInAt ? fmtTime(clockInAt) : '—'}</strong>
                  </div>

                  {/* Notes */}
                  <textarea
                    className="shift-clock__notes"
                    placeholder="Add shift notes (optional)…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                  />

                  {shortOut ? (
                    <div className="shift-clock__short" role="alert">
                      <p>You've only been clocked in {Math.max(0, Math.floor((now - clockInAt) / 60000))} min. Was this a mistake?</p>
                      <div className="shift-clock__actions">
                        <button className="shift-clock__btn shift-clock__btn--break" onClick={handleCancelClockIn} disabled={busy}>
                          🗑 Remove this clock-in
                        </button>
                        <button className="shift-clock__btn shift-clock__btn--out" onClick={doClockOut} disabled={busy}>
                          Clock out anyway
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="shift-clock__actions">
                      {activeShift.status === 'active' ? (
                        <button className="shift-clock__btn shift-clock__btn--break" onClick={handleStartBreak} disabled={busy}>
                          ☕ Start Break
                        </button>
                      ) : (
                        <button className="shift-clock__btn shift-clock__btn--resume" onClick={handleEndBreak} disabled={busy}>
                          ▶ End Break
                        </button>
                      )}
                      <button className="shift-clock__btn shift-clock__btn--out" onClick={handleClockOut} disabled={busy}>
                        ⏹ Clock Out
                      </button>
                    </div>
                  )}
                  {canCancelClockIn && !shortOut && (
                    <button className="shift-clock__mistake" onClick={handleCancelClockIn} disabled={busy}>
                      Clocked in by mistake? Cancel it
                    </button>
                  )}
                </div>
              )}
            </div>

            {notice && <div className="shift-clock__notice" role="status">{notice}</div>}

            {/* Forgot to clock in → ask for the shift instead of punching in/out to "fix" it */}
            <div className="shift-missed">
              <button className="shift-missed__btn" onClick={openMissedModal}>🕘 Forgot to clock in? Request a missed shift</button>
              {myMissed.map((r) => {
                const a = toDate(r.requestedClockIn), b = toDate(r.requestedClockOut);
                return (
                  <div key={r.id} className="shift-missed__item">
                    ⏳ Missed shift {a ? `${fmtShortDate(a)}, ${fmtTime(a)}` : ''}{b ? `–${fmtTime(b)}` : ''} — waiting for approval
                  </div>
                );
              })}
            </div>

            {/* Weekly Summary — the Mon–Sun pay week */}
            <div className="shift-summary">
              <h3 className="shift-summary__title">📊 This Week</h3>
              <div className="shift-summary__stats">
                <div className="shift-summary__stat">
                  <span className="shift-summary__stat-val">{weekShifts.length}</span>
                  <span className="shift-summary__stat-lbl">Shifts</span>
                </div>
                <div className="shift-summary__stat">
                  <span className="shift-summary__stat-val">{fmtMin(weekTotal)}</span>
                  <span className="shift-summary__stat-lbl">Total Hours</span>
                </div>
                <div className="shift-summary__stat">
                  <span className="shift-summary__stat-val">{weekShifts.length > 0 ? `${Math.round(weekTotal / weekShifts.length / 60 * 10) / 10}h` : '—'}</span>
                  <span className="shift-summary__stat-lbl">Avg / Shift</span>
                </div>
              </div>
            </div>

            {/* Recent Shifts */}
            {recentShifts.length > 0 && (
              <div className="shift-history">
                <h3 className="shift-history__title">🕘 Recent Shifts</h3>
                <div className="shift-history__list">
                  {recentShifts.slice(0, 12).map((s) => {
                    const inTime = toDate(s.clockIn);
                    const outTime = toDate(s.clockOut);
                    const req = myRequests[s.id];
                    return (
                      <div key={s.id} className="shift-history__item">
                        <div className="shift-history__date">{fmtShortDate(inTime)}</div>
                        <div className="shift-history__times">
                          {fmtTime(inTime)} — {outTime ? fmtTime(outTime) : '—'}
                        </div>
                        <div className="shift-history__duration">{fmtMin(s.totalMinutes || 0)}</div>
                        {s.notes && <div className="shift-history__notes">📝 {s.notes}</div>}
                        {req?.status === 'pending' ? (
                          <button className="shift-req-badge shift-req-badge--pending shift-req-badge--btn" onClick={() => openReqModal(s)}>
                            ⏳ Change requested · Edit
                          </button>
                        ) : req?.status === 'approved' ? (
                          <div className="shift-req-badge shift-req-badge--approved">✅ Change approved</div>
                        ) : req?.status === 'rejected' ? (
                          <button className="shift-req-badge shift-req-badge--rejected shift-req-badge--btn" onClick={() => openReqModal(s)}>
                            ❌ Change rejected · Request again
                          </button>
                        ) : (
                          <button className="shift-req-btn" onClick={() => openReqModal(s)}>✏️ Request Change</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Hour Change / Missed Shift Request Modal ── */}
      {reqModal && (
        <div className="shift-edit-overlay" onClick={() => !reqSaving && setReqModal(null)}>
          <div className="shift-edit-modal" role="dialog" aria-modal="true" aria-label="Request" onClick={(e) => e.stopPropagation()}>
            {reqResult ? (
              <div className="shift-req-success">
                <div className="shift-req-success__icon">✅</div>
                <h3>{reqResult === 'updated' ? 'Request Updated!' : 'Request Submitted!'}</h3>
                <p>{reqResult === 'updated'
                  ? 'Your waiting request was updated — no duplicate was sent.'
                  : 'A manager will review your request shortly.'}</p>
              </div>
            ) : (
              <>
                <h3 className="shift-edit-modal__title">
                  {reqModal.mode === 'missed' ? '🕘 Request a Missed Shift' : reqModal.pending ? '✏️ Update Your Request' : '✏️ Request Hour Change'}
                </h3>
                {reqModal.mode === 'edit' && (
                  <div className="shift-edit-modal__info">
                    <strong>{fmtTime(toDate(reqModal.shift.clockIn))} — {reqModal.shift.clockOut ? fmtTime(toDate(reqModal.shift.clockOut)) : '—'}</strong>
                    <span>{fmtDate(toDate(reqModal.shift.clockIn))}</span>
                    {reqModal.pending && <span>You already asked for a change — sending again updates that request.</span>}
                  </div>
                )}
                {reqModal.mode === 'missed' && (
                  <div className="shift-edit-modal__info">
                    <strong>Forgot to clock in?</strong>
                    <span>Enter when you actually worked. A manager approves it — no need to clock in and out to fix it.</span>
                  </div>
                )}
                <div className="shift-edit-modal__field">
                  <label className="shift-edit-modal__label" htmlFor="req-in">{reqModal.mode === 'missed' ? 'Started' : 'Corrected Clock In'}</label>
                  <input id="req-in" type="datetime-local" className="shift-edit-modal__input" value={reqClockIn} onChange={(e) => { setReqClockIn(e.target.value); setReqOverlapNote(''); }} />
                </div>
                <div className="shift-edit-modal__field">
                  <label className="shift-edit-modal__label" htmlFor="req-out">{reqModal.mode === 'missed' ? 'Finished' : 'Corrected Clock Out'}</label>
                  <input id="req-out" type="datetime-local" className="shift-edit-modal__input" value={reqClockOut} onChange={(e) => { setReqClockOut(e.target.value); setReqOverlapNote(''); }} />
                </div>
                {reqClockIn && reqClockOut && new Date(reqClockOut) > new Date(reqClockIn) && (
                  <div className="shift-edit-modal__preview">
                    {reqModal.mode === 'missed' ? 'Shift' : 'New total'}: {fmtMin(Math.round((new Date(reqClockOut) - new Date(reqClockIn)) / 60000))}
                  </div>
                )}
                <div className="shift-edit-modal__field">
                  <label className="shift-edit-modal__label" htmlFor="req-reason">Reason <span style={{color:'#ef4444'}}>*</span></label>
                  <textarea
                    id="req-reason"
                    className="shift-edit-modal__input shift-edit-modal__textarea"
                    placeholder={reqModal.mode === 'missed' ? 'e.g. Forgot to clock in when I arrived' : 'e.g. Forgot to clock out, worked extra hour…'}
                    rows={3}
                    value={reqReason}
                    onChange={(e) => setReqReason(e.target.value)}
                  />
                </div>
                {reqError && <div className="shift-req-error" role="alert">{reqError}</div>}
                {reqOverlapNote && !reqError && <div className="shift-req-warn" role="alert">⚠️ {reqOverlapNote}</div>}
                <div className="shift-edit-modal__actions">
                  <button className="shift-edit-modal__btn shift-edit-modal__btn--cancel" onClick={() => setReqModal(null)} disabled={reqSaving}>Cancel</button>
                  <button className="shift-edit-modal__btn shift-edit-modal__btn--save" onClick={handleSubmitRequest} disabled={reqSaving}>
                    {reqSaving ? 'Sending…' : reqOverlapNote ? 'Send anyway' : reqModal.pending ? 'Update Request' : 'Submit Request'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftClock;
